import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import i18next from 'i18next';
import { getCurrencyFormatWithIsoCode } from '../product-detail';
import CategoryHighlighter from '../../category-highlight';
import { convertUTCToLocalDateTime } from '../../../../utils/date-time-utils';
import capitalizeText from '../../../../utils/capitalize-text';
import { capitalizeFirstLetter } from '../../../../utils';
import variables from '../../../../variables.module.scss';
import { orderStatus as orderStatusUtils } from '../../../../shared/shared-function/helperFunctions';
import { TENANT } from '../../../../actions/configuration';
import { getExternalInvoicePdf } from '../../../../actions/api/OrderSummaryAction';
import CustomizedSnackbars from '../../../Shared/Utils/SnackBar';
import './index.scss';
import idConstants from '../../../../data/idConstants';

export const OrderOverview = ({ currentOrderStage, invoiceNetPrice }) => {
  let {
    orderDescription = {},
    billingAddress: userBillingAddress = {},
    shippingAddress: shippingAddress,
    pricingPolicies = [],
    orderStatus = [],
    orderSnapshot = null
  } = useSelector((state) => state.OrderSummaryReducer);
  const getProductDocDetail = useSelector((state) => state.ProductDetailReducer);
  const [data, setData] = useState([]);
  const [filteredPricingPolicies, setFilteredPricingPolicies] = useState(null);
  const [invoiceData, setInvoiceData] = useState({ fileName: null, blobUrl: null });
  const [invoiceSnackbar, setInvoiceSnackbar] = useState({ show: false, message: '', severity: 'error' });
  const isTransformCategory =
    orderDescription?.productDetailsDto?.category?.id ===
    idConstants.transform.categoryId;

  const isUzosTenant =
    orderDescription?.orderSubscriptionMap?.tenantId === TENANT.UZOS;

  const isHardware =
    orderDescription?.productDetailsDto?.category?.displayName ===
    idConstants.hardware.productCategoryName;

  const isUzosHardware = isUzosTenant && isHardware;

  const isPaymentCompleted = orderStatus?.some(
    (step) => step.status === 'PAYMENT' && step.statusUpdateTime !== null
  );

  const shippingChargeDetail = orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.shippingChargeDetail;
  const shippingCharge =shippingChargeDetail?.shippingCharge || 0.00;

  const getCurrencySymbol = (currencyCode) => {
    try {
      return (0).toLocaleString('en', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).replace(/\d/g, '').trim();
    } catch {
      return currencyCode || '';
    }
  };

  useEffect(() => {
    if (getProductDocDetail?.productDocDetail) {
      setData(
        getProductDocDetail?.productDocDetail.filter((document) =>
          [
            idConstants.documentType.certificateDocument,
            idConstants.documentType.termsAndConditionsDocument,
            idConstants.documentType.returnAndReplacementPolicyDocument,
            idConstants.documentType.refundPolicyDocument,
            idConstants.documentType.userManualDocument
          ].includes(document.docTypeId)
        )
      );
    }
  }, [getProductDocDetail]);

  useEffect(() => {
    if (pricingPolicies?.length > 0) {
      const pricingPoliciesDetails = pricingPolicies?.find(
        (planDetails) =>
          planDetails.planId === orderDescription?.productOrderDto?.policyId
      );
      setFilteredPricingPolicies(pricingPoliciesDetails);
    }
  }, [pricingPolicies, orderDescription]);

  // When payment is completed for uzos orders — fetch invoice filename + PDF blob once
  useEffect(() => {
    if (!isUzosTenant || !isHardware || !isPaymentCompleted) return;
    const orderId = orderDescription?.productOrderDto?.productOrderDetailId;
    const tid = orderDescription?.orderSubscriptionMap?.tenantId;
    if (!orderId || !tid) return;

    let cancelled = false;
    (async () => {
      try {
        // Single API call — filename comes from content-disposition header
        const pdfResponse = await getExternalInvoicePdf(orderId, tid);

        // Parse filename from: form-data; name="attachment"; filename="invoice_UFZ/26-27/0162.pdf"
        let fileName = 'invoice.pdf';
        const contentDisposition = pdfResponse.headers?.['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="([^"]+)"/);
          if (match?.[1]) {
            fileName = match[1];
          }
        }

        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);

        if (!cancelled) {
          setInvoiceData({ fileName, blobUrl });
        }
      } catch {
        if (!cancelled) {
          setInvoiceSnackbar({
            show: true,
            message: i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.INVOICE_FETCH_ERROR'),
            severity: 'error'
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isUzosTenant, isPaymentCompleted, orderDescription?.productOrderDto?.productOrderDetailId]);

  // Helper: Unit price display handling slab pricing and currency formatting
  const getUnitPriceDisplay = () => {
    if (!filteredPricingPolicies) return null;
    if (filteredPricingPolicies?.isSlabPricing) {
      return i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.CALCULATED_BASED_ON_SLAB');
    }
    const planCost = Number(filteredPricingPolicies?.planCost);
    const locale = `en-${filteredPricingPolicies?.currency?.currencyCode?.slice(0, 2)}`;
    const fmt = getCurrencyFormatWithIsoCode(
      filteredPricingPolicies?.currency?.currencyCode
    );
    return parseFloat(planCost).toLocaleString(locale, fmt);
  };

  // Helper: Grand total display with plan cost, quantity, invoice fallback
  const getGrandTotalDisplay = () => {
    if (!filteredPricingPolicies) return null;
    const planCost = Number(filteredPricingPolicies?.planCost);
    const qty = Number(orderDescription?.productOrderDto?.cartItemCount) || 0;
    const locale = `en-${filteredPricingPolicies?.currency?.currencyCode?.slice(0, 2)}`;
    const fmt = getCurrencyFormatWithIsoCode(
      filteredPricingPolicies?.currency?.currencyCode
    );
    if (!Number.isNaN(planCost) && planCost > 0) {
      return parseFloat(planCost * qty).toLocaleString(locale, fmt);
    }
    if (invoiceNetPrice != null) {
      return parseFloat(invoiceNetPrice).toLocaleString(locale, fmt);
    }
    return i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.TO_BE_BILLED');
  };

  if (Object.keys(orderDescription)?.length === 0) return null;

  const invoicePaymentStatus = orderDescription?.orderSubscriptionMap?.invoicePaymentStatus;

  const renderInvoicePaymentStatus = () => {
    if (!invoicePaymentStatus) return <span>-</span>;

    if (invoicePaymentStatus === 'PAYMENT_CAPTURED') {
      return (
        <span className='invoice-payment-status invoice-payment-status--success'>
          <i className='a-icon boschicon-bosch-ic-checkmark' />
          {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.PAYMENT_STATUS_SUCCESS')}
        </span>
      );
    }

    if (invoicePaymentStatus === 'PAYMENT_OPEN') {
      return (
        <span className='invoice-payment-status invoice-payment-status--pending'>
          <i className='a-icon boschicon-bosch-ic-clock' />
          {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.PAYMENT_STATUS_PENDING')}
        </span>
      );
    }

    // Any other status (failed, cancelled, etc.)
    return (
      <span className='invoice-payment-status invoice-payment-status--failed'>
        <i className='a-icon boschicon-bosch-ic-alert-error' />
        {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.PAYMENT_STATUS_FAILED')}
      </span>
    );
  };

  const handleInvoiceView = () => {
    if (invoiceData.blobUrl) {
      window.open(invoiceData.blobUrl, '_blank');
    }
  };

  const handleInvoiceDownload = () => {
    if (!invoiceData.blobUrl) return;
    const link = document.createElement('a');
    link.href = invoiceData.blobUrl;
    link.download = invoiceData.fileName || 'invoice.pdf';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showPaymentStatus =
    currentOrderStage.status === orderStatusUtils.ORDER_REJECTED ||
    currentOrderStage.status === orderStatusUtils.CONFIRMED_ORDER_CANCELLED ||
    currentOrderStage.status === orderStatusUtils.ORDER_CANCELLED ||
    currentOrderStage.status === orderStatusUtils.REJECTED_WITH_POD ||
    currentOrderStage.status === orderStatusUtils.BUYER_REJECTED;

  const getBillingAddress = () => {
    const addressObj = userBillingAddress?.shippingAddress;
    let billingAddress = '';
    const firstName = userBillingAddress?.userFirstName;
    const lastName = userBillingAddress?.userLastName;
    if (firstName || lastName) {
      billingAddress += `${firstName || ''} ${lastName || ''}`.trim() + ', ';
    }
    if (addressObj?.addressLine1) {
      billingAddress += addressObj?.addressLine1 + ', ';
    }
    if (addressObj?.addressLine2) {
      billingAddress += addressObj?.addressLine2 + ', ';
    }
    if (addressObj?.cityName) {
      billingAddress += addressObj?.cityName + ', ';
    }
    if (addressObj?.districtName) {
      billingAddress += addressObj?.districtName + ', ';
    }
    if (addressObj?.stateCode) {
      billingAddress += addressObj?.stateCode + ', ';
    }
    if (addressObj?.countryName) {
      billingAddress += addressObj?.countryName + ', ';
    }
    if (addressObj?.postalCode) {
      billingAddress += addressObj?.postalCode + '. ';
    }
    if (billingAddress) return billingAddress;
    else return i18next.t('DYNAMIC_FORM.PRICING_PLAN_DIALOG.NOT_APPLICABLE');
  };

  return (
    <div className='order-overview-container'>
      {invoiceSnackbar.show && (
        <CustomizedSnackbars
          severity={invoiceSnackbar.severity}
          message={invoiceSnackbar.message}
          changeState={() => setInvoiceSnackbar((prev) => ({ ...prev, show: false }))}
        />
      )}
      <div className='order-overview'>
        <div className='order-overview__head order-overview-container'>
          <span className='highlight -mobile'>
            {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.ORDER_ID')}:{' '}
            {isTransformCategory
              ? orderDescription?.productOrderDto?.productOrderId
              : orderDescription?.orderSubscriptionMap?.dcsOrderId}
          </span>
          <span className='-size-s order-overview__head__order-date'>
            <CategoryHighlighter
              categoryColor={variables.badgeMainBg}
              category={` ${i18next.t(
                'DYNAMIC_FORM.SHARED.ORDERED_ON'
              )}: ${convertUTCToLocalDateTime(orderDescription?.orderSubscriptionMap?.orderDate)}`}
            />
          </span>
        </div>
        <div className='order-overview__body'>
          {Object.keys(userBillingAddress)?.length > 0 ? (
            <div className='order-overview__body__address'>
              <div style={{ marginBottom: '1rem' }}>
                {isTransformCategory && (
                  <div className='names-container'>
                    <div>
                      <p className='text-bold'>
                        {i18next.t('DYNAMIC_FORM.PRICING_PLAN_DIALOG.USER_NAME')}
                      </p>
                      <p>
                        {userBillingAddress.userFirstName}{' '}
                        {userBillingAddress.userLastName}
                      </p>
                    </div>
                    <div>
                      <p className='text-bold'>{i18next.t('SHARED.COMPANY_NAME')}</p>
                      <p>{userBillingAddress.companyName}</p>
                    </div>
                  </div>
                )}
                <p className='text-bold'>
                  {capitalizeFirstLetter(
                    i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.BILLING_ADDRESS')
                  )}
                </p>
                <p>{getBillingAddress()}</p>
                <div className='email-container'>
                  <p>{userBillingAddress?.userEmailId}</p>
                  <p>
                    {userBillingAddress?.shippingAddress?.contactNumber ||
                      userBillingAddress?.shippingAddress?.alternateNo}
                  </p>
                  <p>{isTransformCategory && userBillingAddress?.userTelephoneNo}</p>
                </div>
              </div>
              {orderDescription?.productDetailsDto?.category?.displayName ===
                idConstants.hardware.productCategoryName &&
                !isUzosTenant &&
                Object.keys(shippingAddress)?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p className='text-bold'>
                      {capitalizeFirstLetter(
                        i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.SHIPPING_ADDRESS')
                      )}
                    </p>
                    <p>{`${shippingAddress?.houseNumber +
                      ', ' +
                      shippingAddress?.street +
                      ', ' +
                      shippingAddress?.city +
                      ', ' +
                      shippingAddress?.state +
                      ', ' +
                      shippingAddress?.country +
                      ', ' +
                      shippingAddress?.postalCode
                      }`}</p>
                  </div>
                )}
              {isUzosTenant &&
                Object.keys(shippingAddress)?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p className='text-bold'>
                      {capitalizeFirstLetter(
                        i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.SHIPPING_ADDRESS')
                      )}
                    </p>
                    {(shippingAddress?.firstName || shippingAddress?.lastName) && (
                      <p>{`${shippingAddress?.firstName || ''} ${shippingAddress?.lastName || ''}`.trim()}</p>
                    )}
                    <p>{`${shippingAddress?.houseNumber +
                      ', ' +
                      shippingAddress?.street +
                      ', ' +
                      shippingAddress?.city +
                      ', ' +
                      shippingAddress?.state +
                      ', ' +
                      shippingAddress?.country +
                      ', ' +
                      shippingAddress?.postalCode
                      }`}</p>
                  </div>
                )}
              {/*commenting the code as paymentMode has to be changed to payment type but still need some Confirmation  */}
              {/* <p>
                <span className='text-bold'>
                  {capitalizeFirstLetter(i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.PAYMENT_METHOD'))}{' '}
                  :
                </span>{' '}
                {orderDescription?.paymentMode}
              </p> */}
            </div>
          ) : null}
          <hr className='a-divider a-divider--vertical' />
          <div className='order-overview__body__summary'>
            {isUzosHardware ? (
              /* UZOS Hardware: Payment Status → Unit Price → Subtotal → Quantity → Taxes → Shipping → Grand Total */
              <div className='order-overview__body__flex'>
                {!showPaymentStatus && (
                  <p className='payment-status margin_top_bottom'>
                    <span className='text-bold'>
                      {capitalizeFirstLetter(
                        i18next.t('DYNAMIC_FORM.SHARED.PAYMENT_STATUS')
                      )}{' '}
                      :
                    </span>
                    {renderInvoicePaymentStatus()}
                  </p>
                )}
                <p className='payment-status margin_top_bottom'>
                  <span className='text-bold'>
                    {capitalizeFirstLetter(i18next.t('ORDER.UNIT_PRICE'))}
                  </span>
                  <span>
                    {orderSnapshot?.itemOrderDetails?.unitPrice != null
                      ? `${getCurrencySymbol(orderSnapshot.itemOrderDetails.itemPaymentDetails?.currency)} ${Number(orderSnapshot.itemOrderDetails.unitPrice).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </span>
                </p>
                <p className='payment-status margin_top_bottom'>
                  <span className='text-bold'>
                    {capitalizeFirstLetter(
                      i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.ORDER_QUANTITY')
                    )}
                  </span>
                  <span>
                    {orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.quantity}
                  </span>
                </p>
                <p className='payment-status margin_top_bottom'>
                  <span className='text-bold'>
                    {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.SUBTOTAL') || 'Subtotal'}
                  </span>
                  <span>
                    {orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.itemSubtotal != null
                      ? `${getCurrencySymbol(orderSnapshot.itemOrderDetails.itemPaymentDetails.currency)} ${Number(orderSnapshot.itemOrderDetails.itemPaymentDetails.itemSubtotal).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </span>
                </p>
                {orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.discountAmount && orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.discountAmount !== '0.00' &&
                  <p className='payment-status margin_top_bottom'>
                    <span className='text-bold'>
                      {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.DISCOUNT')}
                    </span>
                    <span className='discount'>
                      - {`${getCurrencySymbol(orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.currency)} ${Number(orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.discountAmount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`}
                    </span>
                  </p>
                }
                {orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.subtotalAfterDiscount && orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.discountAmount && orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.discountAmount !== '0.00' &&
                  <p className='payment-status margin_top_bottom'>
                    <span className='text-bold'>
                      {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.SUBTOTAL_AFTER_DISCOUNT')}
                    </span>
                    <span>
                      {`${getCurrencySymbol(orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.currency)} ${Number(orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.subtotalAfterDiscount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`}
                    </span>
                  </p>
                }
                <p className='payment-status margin_top_bottom'>
                  <span className='text-bold'>
                    {capitalizeFirstLetter(
                      i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.TAXES')
                    )}
                    {
                      orderSnapshot?.itemOrderDetails?.gstPercentage && orderSnapshot?.itemOrderDetails?.gstPercentage > 0 &&
                      <span className='tax-rate-text'>
                        {` (${orderSnapshot.itemOrderDetails.gstPercentage}%)`}
                      </span>
                    }
                  </span>
                  <span>
                    {orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.taxAmount != null
                      ? `${getCurrencySymbol(orderSnapshot.itemOrderDetails.itemPaymentDetails.currency)} ${Number(orderSnapshot.itemOrderDetails.itemPaymentDetails.taxAmount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : filteredPricingPolicies?.taxAmount != null
                        ? filteredPricingPolicies.taxAmount
                        : i18next.t('DYNAMIC_FORM.PRICING_PLAN_DIALOG.NOT_APPLICABLE')}
                  </span>
                </p>
                <p className='payment-status margin_top_bottom'>
                  <span className='text-bold'>
                    {capitalizeFirstLetter(
                      i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.SHIPPING_CHARGES')
                    )}
                  </span>
                  <span>
                    {`${getCurrencySymbol(orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.currency)} ${Number(shippingCharge).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                    }
                  </span>
                </p>
                {
                  shippingChargeDetail?.shippingChargeGst && shippingChargeDetail.shippingChargeGst > 0 &&
                  <p className='payment-status margin_top_bottom'>
                    <span className='text-bold'>
                      {capitalizeFirstLetter(
                        i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.TAX_ON_SHIPPING_CHARGES')
                      )}
                      {
                        shippingChargeDetail?.shippingTaxRate && shippingChargeDetail.shippingTaxRate > 0 &&
                        <span className='tax-rate-text'>
                          {` (${shippingChargeDetail.shippingTaxRate}%)`}
                        </span>
                      }
                    </span>
                    <span>
                      {`${getCurrencySymbol(orderSnapshot.itemOrderDetails.itemPaymentDetails.currency)} ${Number(shippingChargeDetail.shippingChargeGst).toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}`
                      }
                    </span>
                  </p>
                }
                <hr className='order-overview__body__summary__divider' />
                <p className='order-overview__body__summary--flex item-grand-total margin_top_bottom'>
                  <span>
                    {capitalizeFirstLetter(
                      i18next.t('DYNAMIC_FORM.SHARED.GRAND_TOTAL')
                    )}{' '}
                    :{' '}
                  </span>
                  <span>
                    {orderSnapshot?.itemOrderDetails?.itemPaymentDetails?.itemTotalAmount != null
                      ? `${getCurrencySymbol(orderSnapshot.itemOrderDetails.itemPaymentDetails.currency)} ${Number(orderSnapshot.itemOrderDetails.itemPaymentDetails.itemTotalAmount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </span>
                </p>
              </div>
            ) : (
              /* Default: Unit Price → Order Quantity → Grand Total → Taxes → Payment Status */
              <p className='order-overview__body__flex'>
                <p className='payment-status'>
                  <span className='text-bold'>
                    {capitalizeFirstLetter(i18next.t('ORDER.UNIT_PRICE'))}
                  </span>
                  {filteredPricingPolicies && <span> {getUnitPriceDisplay()} </span>}
                </p>
                <div className='margin_top_bottom'>
                  <p className='payment-status'>
                    <span className='text-bold'>
                      {capitalizeFirstLetter(
                        i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.ORDER_QUANTITY')
                      )}
                    </span>
                    <span> {orderDescription?.productOrderDto?.cartItemCount} </span>
                  </p>
                </div>
                <div className='margin_top_bottom'>
                  <p className='order-overview__body__summary--flex item-grand-total'>
                    <span>
                      {' '}
                      {capitalizeFirstLetter(
                        i18next.t('DYNAMIC_FORM.SHARED.GRAND_TOTAL')
                      )}{' '}
                      :{' '}
                    </span>
                    {filteredPricingPolicies &&
                      orderDescription?.productOrderDto?.cartItemCount &&
                      filteredPricingPolicies?.currency?.currencyCode && (
                        <span>{getGrandTotalDisplay()}</span>
                      )}
                  </p>
                  <p className='order-overview__body__summary__tax-text'>
                    **
                    {i18next.t(
                      'ORDER_MANAGEMENT.ORDER_OVERVIEW.TAXES_AND_SHIPPING_CHARGES_INCLUDED'
                    )}
                  </p>
                </div>
                {!showPaymentStatus && (
                  <p
                    className={
                      'order-overview__body__summary--flex item-grand-total payment-type'
                    }
                  >
                    <span>
                      {capitalizeFirstLetter(
                        i18next.t('DYNAMIC_FORM.SHARED.PAYMENT_STATUS')
                      )}{' '}
                      :
                    </span>
                    <span>
                      {orderDescription?.orderSubscriptionMap?.paymentStatus
                        ? capitalizeText(
                          orderDescription?.orderSubscriptionMap?.paymentStatus
                        )
                        : 'Open'}
                    </span>
                  </p>
                )}
              </p>
            )}
          </div>
          <hr className='a-divider a-divider--vertical' />
          <div className='order-overview__body__documents'>
            <div>
              <p className='text-bold'>
                {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.DOCUMENTS')}
              </p>
              <div className='order-overview__body__documents__list'>
                {data?.length === 0 ? (
                  <p>{i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.NO_DOCUMENTS')}.</p>
                ) : (
                  data?.map((item) => (
                    <div
                      className='order-overview__body__documents__list--item'
                      key={item.index}
                    >
                      <span className='a-link'>
                        <a
                          href={item?.blobFileUrl}
                          className='product-documents'
                          target='_blank'
                          rel='noreferrer'
                          download
                        >
                          {' '}
                          <i
                            className={`a-icon boschicon-bosch-ic-document-${'pdf'}`}
                          />{' '}
                        </a>{' '}
                      </span>
                      <span className='doc-name'>{item?.documentName}</span>
                    </div>
                  ))
                )}
              </div>
              {isUzosHardware && isPaymentCompleted && (
                <div className='order-overview__body__documents__invoice'>
                  <p className='text-bold'>
                    {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.INVOICE')}
                  </p>
                  <div className='order-overview__body__documents__list'>
                    <div className='order-overview__body__documents__list--item order-overview__body__documents__invoice__item'>

                      <div className='order-overview__body__documents__invoice__row'>
                        <span className='a-link'>
                          <i className='a-icon boschicon-bosch-ic-document-pdf' />
                        </span>
                        {invoiceData.fileName ? (
                          <span className='order-overview__body__documents__invoice__filename'>
                            {invoiceData.fileName}
                          </span>
                        ) : (
                          <span className='order-overview__body__documents__invoice__no-invoice'>
                            {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.NO_INVOICE')}
                          </span>
                        )}
                      </div>

                      {invoiceData.blobUrl && (
                        <div className='order-overview__body__documents__invoice__actions'>
                          <a
                            href='#'
                            onClick={(e) => {
                              e.preventDefault();
                              handleInvoiceView();
                            }}
                            className='order-overview__body__documents__invoice__action-link'
                          >
                            <i className='a-icon boschicon-bosch-ic-document-view' />
                            {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.VIEW')}
                          </a>
                          <a
                            href='#'
                            className='order-overview__body__documents__invoice__action-link'
                            onClick={(e) => {
                              e.preventDefault();
                              handleInvoiceDownload();
                            }}
                          >
                            <i className='a-icon boschicon-bosch-ic-document-arrow-down' />
                            {i18next.t('ORDER_MANAGEMENT.ORDER_OVERVIEW.DOWNLOAD')}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderOverview;
